import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'
import { ExportDocumentsPresenter } from 'src/components/exportdocuments/export_documents_presenter'

import GroupProfileKeys from '../group-profile/gp_keys'
import { WaitingScope } from '../the-connection/tc_scopes'
import UserProfileKeys from './up_keys'
import { HeaderScope, UserProfileScope, type HistoryQueryFilter } from './up_scopes'
import { UserProfileService } from './up_service'
import { UserChangePasswordFormKeys } from '../admin/user-manager/form/change-password'

const LOG = Logger.get('UserProfilePresenter')
const userProfileService = UserProfileService.singleton()

export class UserProfilePresenter extends CubePresenter<MainPresenter, UserProfileScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, UserProfilePresenter>

    private readonly __exportDocsPresenter: ExportDocumentsPresenter

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new UserProfileScope())
        this.__headerBar = new Presenter(this, this.scope.header)
        this.__exportDocsPresenter = new ExportDocumentsPresenter(this, this.scope.exportDocsScope)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        this.__exportDocsPresenter.release()
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new UserProfileKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        if (last) {
            keys.dialogSlot(undefined)
        }

        if (
            (keys.startMonthFilter && this.scope.filterScope.filters.startMonth !== keys.startMonthFilter) ||
            this.scope.filterScope.filters.endMonth !== keys.endMonthFilter ||
            this.scope.filterScope.filters.page !== keys.pageFilter ||
            this.scope.filterScope.filters.limit !== keys.limitFilter
        ) {
            this.scope.filterScope.filters.startMonth =
                keys.startMonthFilter || this.scope.filterScope.filters.startMonth
            this.scope.filterScope.filters.endMonth = keys.endMonthFilter
            this.scope.filterScope.filters.page = keys.pageFilter || this.scope.filterScope.filters.page
            this.scope.filterScope.filters.limit = keys.limitFilter || this.scope.filterScope.filters.limit
        }

        if (!this.scope.profileScope.profile || !this.scope.profileScope.profile.id) {
            await this.handleFind(this.scope.filterScope.filters)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new UserProfileKeys(this.app, intent)
        keys.startMonthFilter = this.scope.filterScope.filters.startMonth
        keys.endMonthFilter = this.scope.filterScope.filters.endMonth
        keys.pageFilter = this.scope.filterScope.filters.page
        keys.limitFilter = this.scope.filterScope.filters.limit
    }

    async #initialize() {
        LOG.debug('initialize')
        this.scope.filterScope.onFind = this.action(this.handleFind)
        this.scope.onOpenGroupProfile = this.action(this.handleOpenGroupProfile)
        this.scope.onOpenChangePassword = this.action(this.handleOpenChangePassword)
        this.scope.historyScope.onViewDocuments = this.action(this.handleViewDocuments)
        this.__exportDocsPresenter.initialize((exportId, query) =>
            userProfileService.fetchExportDocuments(exportId, query)
        )
    }

    private async handleViewDocuments(exportId: number) {
        await this.__exportDocsPresenter.open(exportId)
    }

    private async handleFind(filters: Partial<HistoryQueryFilter>) {
        this.scope.filterScope.filters = { ...this.scope.filterScope.filters, ...filters }

        await Promise.all([this.handleGetProfile(), this.handleFindHistory(), this.handleFindChartData()])
    }

    private async handleOpenGroupProfile(groupId: number) {
        const key = new GroupProfileKeys(this.app)
        key.groupId = groupId
        key.origin = 'user-profile'
        await key.flip()
    }

    private async handleOpenChangePassword() {
        const key = new UserChangePasswordFormKeys(this.app)
        key.userId = this.scope.profileScope.profile.id
        key.origin = 'user-profile'
        await key.flip()
    }

    private async handleGetProfile() {
        if (this.scope.profileScope.profile && this.scope.profileScope.profile.id) {
            return
        }
        try {
            this.scope.profileScope.waiting = new WaitingScope()
            this.scope.profileScope.profile = await userProfileService.fetchProfile()
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.profileScope.waiting = undefined
        }
    }

    private async handleFindHistory() {
        try {
            this.scope.historyScope.history_working = true
            this.scope.historyScope.history = await userProfileService.fetchHistory(this.scope.filterScope.filters)
            this.scope.historyScope.historyVersion++
        } finally {
            this.scope.historyScope.history_working = false
        }
    }

    private async handleFindChartData() {
        try {
            this.scope.chartScope.history_chart_working = true
            this.scope.chartScope.historyChart = await userProfileService.fetchChartHistory(
                this.scope.filterScope.filters
            )
        } finally {
            this.scope.chartScope.history_chart_working = false
        }
    }
}

export default UserProfilePresenter
